import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listEvents, type CoreEventDto } from '@/lib/api/core';

/**
 * useEvents — lista pública de eventos próximos vía el proxy de api-core
 * (`GET /v1/events`), que internamente lee el catálogo de api-taquilla y
 * filtra por estado publicado / territorio / rol.
 *
 * Criterio "disponible": el proxy ya devuelve eventos `published`; acá sólo
 * filtramos los que aún no terminaron (corte = inicio del día de hoy, usando
 * `end_date` o, en su defecto, `date`).
 *
 * `price` viene en pesos colombianos enteros — se pasa directo a `formatCOP`.
 */

export type EventTranslations = Record<string, { title?: string; description?: string }> | null;

export type EventListItem = {
  id: string;
  title: string;
  slug: string | null;
  subtitle: string | null;
  description: string | null;
  date: string | null;
  endDate: string | null;
  venue: string | null;
  priceCents: number | null;
  coverImage: string | null;
  status: string | null;
  /** ISO-4217 del evento (hereda de la marca); para formatear precios. */
  currency: string | null;
  /** { en: { title, description } } — localiza el contenido por idioma. */
  translations: EventTranslations;
  /** Categoría (id estable + nombre legible) — para agrupar `/eventos/categorias`. */
  categoryId: string | null;
  categoryName: string | null;
  /** Ciudad (id estable + nombre legible) — para agrupar `/eventos/ciudades`. */
  cityId: string | null;
  cityName: string | null;
};

/**
 * Localiza título/descripción de un evento por idioma con fallback al español
 * base. No muta el objeto original.
 */
export function localizeEventContent(
  ev: Pick<EventListItem, 'title' | 'description' | 'translations'>,
  language: string,
): { title: string; description: string | null } {
  const lang = (language || 'es').toLowerCase().slice(0, 2);
  const tr = ev.translations?.[lang];
  return {
    title: tr?.title?.trim() || ev.title,
    description: tr?.description?.trim() || ev.description,
  };
}

/** Normaliza un valor a string no-vacío o null (para ids/nombres opcionales). */
function _str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function priceFrom(r: CoreEventDto): number | null {
  const candidates: unknown[] = [
    (r as Record<string, unknown>).display_from_price,
    (r as Record<string, unknown>).min_localidad_price,
    r.price,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const n = typeof c === 'string' ? Number.parseFloat(c) : Number(c);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  return null;
}

async function fetchEvents(): Promise<EventListItem[]> {
  const data = await listEvents();

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const cutoff = startOfToday.getTime();

  return (
    data
      // SPEC-010 (b) — la tienda de Mi Boleta es de BOLETERÍA: un evento solo-consumo
      // (`uses_ticketing=false`) no se lista acá (sí en la web fan de FC). El listado
      // público compartido ahora los incluye; commerce los oculta client-side.
      .filter((r) => r.uses_ticketing !== false)
      .map((r) => {
        const cover = typeof r.cover_image === 'string' ? r.cover_image.trim() : '';
        const venue = typeof r.venue === 'string' ? r.venue.trim() : '';
        const subtitle = typeof r.short_description === 'string' ? r.short_description.trim() : '';
        return {
          id: String(r.id),
          title: typeof r.title === 'string' && r.title.trim() ? r.title : 'Evento',
          slug: (r.slug as string | null) ?? null,
          subtitle: subtitle || null,
          description: (r.description as string | null) ?? null,
          date: (r.date as string | null) ?? null,
          endDate: (r.end_date as string | null) ?? null,
          venue: venue || null,
          priceCents: priceFrom(r),
          coverImage: cover || null,
          status: (r.status as string | null) ?? null,
          currency:
            typeof (r as Record<string, unknown>).currency === 'string'
              ? ((r as Record<string, unknown>).currency as string)
              : null,
          translations: ((r as Record<string, unknown>).translations as EventTranslations) ?? null,
          categoryId: _str((r as Record<string, unknown>).category_id),
          categoryName: _str((r as Record<string, unknown>).category_name),
          cityId: _str((r as Record<string, unknown>).city_id),
          cityName: _str((r as Record<string, unknown>).city_name),
        };
      })
      .filter((e) => {
        const endRef = e.endDate ?? e.date;
        if (!endRef) return true;
        const t = new Date(endRef).getTime();
        return Number.isFinite(t) ? t >= cutoff : true;
      })
  );
}

export function useEvents(): UseQueryResult<EventListItem[]> {
  return useQuery({
    queryKey: ['events', 'list', 'published-upcoming'],
    queryFn: fetchEvents,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
